import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowUpRightIcon } from "lucide-react";
import { thoughts, thoughtMeta } from "../data/thoughts";
import { BODY, DISPLAY_HEADING, H2, LEAD } from "../utils/typography";
import { ThoughtBody } from "../components/ThoughtBody";
import { Reveal } from "../components/Reveal";
import { usePageTitle } from "../hooks/usePageTitle";
export function ThoughtPost() {
  const {
    slug
  } = useParams();
  const index = thoughts.findIndex(t => t.slug === slug);
  const post = thoughts[index];
  usePageTitle(post ? post.title : "Thought not found");
  if (!post) {
    return <main id="main-content" tabIndex={-1} className="flex min-h-screen w-full flex-col items-start justify-center bg-cream px-6 md:px-12 lg:px-20" data-id="element-529" data-mp="iead5lg5fgym">
        <p className="text-sm font-medium uppercase tracking-wide text-rust" data-id="element-530" data-mp="1tvjsbvs8vmu4">Not found</p>
        <h1 className={`mt-4 ${DISPLAY_HEADING} text-espresso`} data-id="element-531" data-mp="1ytbv5kufgtd3">
          This thought wandered off.
        </h1>
        <Link to="/thoughts" className="mt-10 inline-flex items-center gap-2 rounded-full bg-espresso px-6 py-3 text-sm font-medium uppercase text-cream transition-colors duration-150 hover:bg-rust" data-id="element-532" data-mp="1f2xcnueif65i">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-533" data-mp="160fv3t9sv1ou" />
          All thoughts
        </Link>
      </main>;
  }
  const isPublished = post.body.length > 0 && !post.status;
  const next = thoughts.length > 1 ? thoughts[(index + 1) % thoughts.length] : undefined;
  return <main id="main-content" tabIndex={-1} className="w-full bg-cream pb-24 pt-36 md:pb-32 md:pt-48 lg:pb-40" data-id="element-534" data-mp="rcutk3skpaj1">
      <article data-id="element-535" data-mp="met85c7ocz4e">
        <Reveal className="px-6 md:px-12 lg:px-20" data-id="element-536" data-mp="mejjpt2a300t">
          <header data-id="element-537" data-mp="icy7m9xkipro">
          <Link to="/thoughts" className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-espresso/70 transition-colors duration-150 hover:text-rust" data-id="element-538" data-mp="idg1izwrcrwf">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-539" data-mp="8equ612hmpuf" />
            All thoughts
          </Link>
          <p className="mt-10 text-sm font-medium uppercase tracking-wide text-rust" data-id="element-540" data-mp="3uv7oryqr2lm9">
            {post.category}
          </p>
          <h1 className={`mt-4 max-w-[16ch] ${DISPLAY_HEADING} text-espresso`} data-id="element-541" data-mp="3nmr7hujckqe">
            {post.title}
          </h1>
          <p className="mt-8 text-espresso/70" data-id="element-542" data-mp="3pwxstlx7bgzl">{thoughtMeta(post)}</p>
          </header>
        </Reveal>

        {post.image && <Reveal className="mt-12 px-6 md:mt-16 md:px-12 lg:px-20" delay={0.06} data-id="element-543" data-mp="hgujzcucrm9o">
            <img src={post.image} alt={post.title} decoding="async" loading="lazy" width={1600} height={900} className="aspect-[16/9] w-full rounded-3xl bg-paper object-cover" data-id="element-544" data-mp="mgqnzc31vdp8" />
          </Reveal>}

        <div className="mx-auto mt-16 max-w-3xl px-6 md:mt-24" data-id="element-545" data-mp="10fqg4tpruaq5">
          <Reveal delay={0.1} data-id="element-546" data-mp="156owtueo9atx">
            <p className={`${LEAD} text-espresso`} data-id="element-547" data-mp="156ykvcg5p7wz">{post.excerpt}</p>
          </Reveal>

          {isPublished ? <div className="mt-14" data-id="element-548" data-mp="2pvsxfy2i8j4h">
              <ThoughtBody blocks={post.body} data-id="element-549" data-mp="2pur9wu4ay45v" />
            </div> : <div className="mt-14 border-t border-espresso/10 pt-8" data-id="element-550" data-mp="2kxr50mj6v7gc">
              <p className="text-sm font-medium uppercase tracking-wide text-espresso/70" data-id="element-551" data-mp="2zlvpnw1sxt1r">
                {post.status ?? "Coming soon"}
              </p>
              <p className={`mt-3 ${BODY} text-espresso/80`} data-id="element-552" data-mp="26y88ys3njmke">
                I’m still working through this one.
              </p>
              <Link to="#contact" className="mt-8 inline-flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-espresso px-6 text-sm font-medium uppercase text-cream transition-colors duration-150 hover:bg-rust" data-id="element-553" data-mp="21zyeyktnys0d">
                Let’s talk
                <ArrowUpRightIcon className="h-4 w-4" data-id="element-554" data-mp="3rdbjob5oui20" />
              </Link>
            </div>}
        </div>
      </article>

      {next && next.slug !== post.slug && <nav aria-label="Next thought" className="mx-6 mt-24 md:mx-12 md:mt-32 lg:mx-20 lg:mt-40" data-id="element-555" data-mp="mhjxrtu0rzhx">
          <Link to={`/thoughts/${next.slug}`} className="group flex items-end justify-between gap-6 border-t border-espresso/10 pt-10" data-id="element-556" data-mp="qilwm9k0y9jd">
            <div data-id="element-557" data-mp="vk1ghyagua06">
              <p className="text-sm font-medium uppercase tracking-wide text-espresso/70" data-id="element-558" data-mp="10h1myab89ert">
                Next thought
              </p>
              <p className={`mt-3 ${H2} text-espresso transition-colors duration-150 group-hover:text-rust`} data-id="element-559" data-mp="15f3b39nig4a5">
                {next.title}
              </p>
            </div>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-espresso text-cream transition-colors duration-150 group-hover:bg-rust" data-id="element-560" data-mp="1e7y5177j2dkm">
              <ArrowUpRightIcon className="h-5 w-5" data-id="element-561" data-mp="1j9doiuu24c0w" />
            </span>
          </Link>
        </nav>}
    </main>;
}